import React, { useContext, useMemo, MouseEvent, useState } from 'react';
import { AlertService, Timing, unitPriceCalculation } from '@spoiler-alert/ui-library';
import { useMutation, type InternalRefetchQueriesInclude } from '@apollo/client';
import { ActiveOfferShiftsQuery, AwardSummaryQuery, OfferListingsByInventoryQuery, trucklanePollingQuery } from '../../../graphql/queries';
import { TrucklaneActionInProgressContext } from '../../trucklane/trucklane-utility';
import OfferCardLayout from './offer-card-layout';
import type {
  AcceptShiftedOfferListingsArgs,
  AcceptShiftedOfferListingsResponse,
  OfferCardActiveTrucklane,
  OfferCardUser,
  ProposedOfferShift,
} from './offer-card-types';
import OfferShiftContext from '../offer-shift-context';
import { Inventory, Listing, OfferListing } from '../../../graphql/query-result-types';
import { POSSIBLE_ACTIONS } from './offer-card-enums';
import Tracking from '../../../utilities/tracking-apis';
import { AcceptShiftedOfferListings } from '../../../graphql/mutations';
import deleteCachedFieldsOnOfferReviewPollingQuery from '../../../apollo/cache-helpers/delete-cached-fields-on-offer-review-polling-query';
import deleteCachedFieldsOnUserQuery from '../../../apollo/cache-helpers/delete-cached-fields-on-user-query';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles(
  {
    miniTooltipWrap: {
      width: 200,
      whiteSpace: 'wrap',
    },
  },
  { name: 'ShiftDestinationOfferCard' },
);

interface ShiftDestinationOfferCardProps {
  setOfferCardsDisabled: (disabled: boolean) => void;
  inventoryId: string;
  originalOfferListing: OfferListing;
  inventory: Inventory;
  user: OfferCardUser;
  fullstoryTag?: string;
  buyerSite: {
    _id: string;
    siteName: string;
  };
  activeTruckLane: OfferCardActiveTrucklane;
  listing: Listing;
  shift: ProposedOfferShift;
  showSuggestions: boolean;
  additionalRefetchQueries?: InternalRefetchQueriesInclude;
  onSelectInventory?: (inventoryId: string) => void;
  onOptimisticAward?: (shift: ProposedOfferShift) => void;
  onAwardSettled?: (shiftId: string) => void;
}

const ShiftDestinationOfferCard: React.FC<ShiftDestinationOfferCardProps> = ({
  setOfferCardsDisabled,
  originalOfferListing,
  inventory,
  listing,
  buyerSite,
  shift,
  activeTruckLane,
  user,
  showSuggestions,
  additionalRefetchQueries,
  fullstoryTag,
  onSelectInventory,
  onOptimisticAward,
  onAwardSettled,
}) => {
  const classes = useStyles();
  const quantity = shift.shiftedQuantity;
  const [unitPrice, setUnitPrice] = useState<number>(unitPriceCalculation(shift.unitPrice * quantity, quantity, originalOfferListing.pricingDivisor));
  const totalPrice = quantity * (unitPrice * (originalOfferListing.pricingDivisor || 1));

  const [acceptOfferShift] = useMutation<AcceptShiftedOfferListingsResponse, AcceptShiftedOfferListingsArgs>(AcceptShiftedOfferListings);

  const { setActionInProgress, actionInProgress } = useContext(TrucklaneActionInProgressContext);
  const offerShiftContextValue = useContext(OfferShiftContext);

  const refetchQueries = () => {
    const queries: InternalRefetchQueriesInclude = [
      {
        query: AwardSummaryQuery,
      },
      {
        query: ActiveOfferShiftsQuery,
      },
      {
        query: OfferListingsByInventoryQuery,
        variables: { inventoryIds: [inventory._id] },
      },
    ];
    if (showSuggestions) {
      queries.push({
        query: trucklanePollingQuery,
        variables: { pollingDetails: { origin: inventory.siteId, truckType: inventory.truckType } },
      });
    }
    if (additionalRefetchQueries) {
      queries.push(...additionalRefetchQueries);
    }
    return queries;
  };

  const handleError = (message: string): void => {
    AlertService.alert({ type: 'warning', autoDismiss: true, message: <span>{message}</span>, dismissDelay: 6000 });
  };

  const handleAcceptOfferShift = Timing.throttle(() => {
    if (actionInProgress) return;
    if (setActionInProgress) setActionInProgress(true);
    setOfferCardsDisabled(true);
    onOptimisticAward?.(shift);
    acceptOfferShift({
      variables: {
        offerShifts: [
          {
            shiftedOfferListings: [
              {
                id: shift._id,
                shiftedInventoryId: inventory._id,
                pricePerCase: unitPrice * (originalOfferListing.pricingDivisor || 1),
                shiftedQuantity: shift.shiftedQuantity,
                logisticsOption: shift.newLogisticsRelationshipName,
                shiftType: shift.shiftType,
                originalOfferId: originalOfferListing._id,
              },
            ],
            buyerSiteName: buyerSite.siteName,
          },
        ],
      },
      update: (cache) => {
        deleteCachedFieldsOnUserQuery(cache, ['awardedOfferListings', 'awardedInventoryFilterParameters']);
        deleteCachedFieldsOnOfferReviewPollingQuery(cache);
      },
      refetchQueries: refetchQueries(),
      awaitRefetchQueries: true,
    })
      .then((res) => {
        if (res.data?.acceptShiftedOfferListings.errors && res.data.acceptShiftedOfferListings.errors.length > 0) {
          handleError(res.data.acceptShiftedOfferListings.errors[0].message);
          onAwardSettled?.(shift._id);
          return;
        }
        Tracking.Event(`Offer Shift was accepted ${fullstoryTag || ''}`, {
          userId: user._id,
          shiftId: shift._id,
        });
        onAwardSettled?.(shift._id);
      })
      .catch(() => {
        handleError(
          'We were unable to award this offer due to an unknown error. Our team has been notified and are looking into the issue. Please contact customer support if the issue persists.',
        );
        onAwardSettled?.(shift._id);
      })
      .finally(() => {
        setOfferCardsDisabled(false);
        if (setActionInProgress) setActionInProgress(false);
      });
  }, 1000);

  const contextualShifts = useMemo(() => {
    if (offerShiftContextValue?.shiftMap.has(shift.originalOfferListingId)) {
      return offerShiftContextValue.shiftMap.get(shift.originalOfferListingId) || [];
    }
    return [];
  }, [shift, offerShiftContextValue]);

  const handleOfferShiftViewClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
    offerShiftContextValue?.setCurrentShifts(contextualShifts, shift.originalOfferListing.inventoryId);
    onSelectInventory?.(shift.originalOfferListing.inventoryId);
  };

  const offerInfo = useMemo(
    () => ({
      ...originalOfferListing,
      suggestions: {
        award: {
          quantity: quantity,
          suggested: true,
          updatedAt: shift.updatedAt,
        },
      },
      quantity: quantity,
      originalQuantity: quantity,
      totalPrice: totalPrice,
      originalTotalPrice: shift.unitPrice * shift.shiftedQuantity,
      trucklaneDistance: shift.shiftTrucklaneDistance,
      trucklaneCost: shift.shiftTrucklaneCost,
      activeReservePrice: shift?.shiftInventory?.activeReservePricing?.reservePrice || originalOfferListing.activeReservePrice,
      _id: `shift-${shift._id}`,
      status: 'ACTIVE',
    }),
    [
      originalOfferListing,
      quantity,
      shift.updatedAt,
      shift.unitPrice,
      shift.shiftedQuantity,
      shift.shiftTrucklaneDistance,
      shift.shiftTrucklaneCost,
      shift._id,
      totalPrice,
    ],
  );

  const enabledActions = showSuggestions ? [POSSIBLE_ACTIONS.AWARD, POSSIBLE_ACTIONS.EDIT_PRICE] : [];

  const actionTooltipOverrides = showSuggestions
    ? {}
    : {
        [POSSIBLE_ACTIONS.AWARD]: {
          text: <div className={classes.miniTooltipWrap}>You must recalculate suggested awards to award.</div>,
          textWidth: 200,
        },
      };

  return (
    <OfferCardLayout
      styles={{}}
      disabled={false}
      offerInfo={offerInfo}
      inventory={inventory}
      listing={listing}
      buyerSite={buyerSite}
      activeTruckLane={activeTruckLane}
      user={user}
      showSuggestions={showSuggestions}
      showNegotiationSuggestions={false}
      refetchQueries={refetchQueries}
      handleAward={handleAcceptOfferShift}
      handleUnaward={() => {}}
      handleIgnore={() => {}}
      handleRestore={() => {}}
      handleNegotiation={() => {}}
      handlePostAwardUpdateBid={() => {}}
      handleUnawardAndNegotiate={() => {}}
      showWarningModal={false}
      setShowWarningModal={() => {}}
      quantity={quantity}
      setQuantity={() => {}}
      unitPrice={unitPrice}
      setUnitPrice={setUnitPrice}
      actionInProgress={actionInProgress}
      ignoreInProgress={false}
      negotiationInProgress={false}
      incomingOfferShift={shift}
      outgoingShifts={[]}
      handleOfferShiftViewClick={handleOfferShiftViewClick}
      enabledActions={enabledActions}
      actionTooltipOverrides={actionTooltipOverrides}
    />
  );
};

export default ShiftDestinationOfferCard;
